import { Box, Button, Typography, useTheme } from "@mui/material";
import BoxHeader from "components/BoxHeader";
import FlexBetween from "components/FlexBetween";
import Header from "components/Header";
import StudentReport from "components/StudentReport";
import { Age } from "components/StudentsGrid";
import SummaryDataBox from "components/SummaryDataBox";
import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { Cell, Pie, PieChart } from "recharts";
import {
    useGetStudentDetailQuery,
    useLazyGetStudentReportPdfQuery,
} from "state/api";
import { saveAs } from "file-saver";
import { useReactToPrint } from "react-to-print";
import { useNavigate } from "react-router-dom";
import ReportButton from "components/ReportButton";
import ObservationModal from "components/ObservationModal";
import { Student } from "types/Student";

const StudentDetailPage = () => {
    const { palette } = useTheme();
    const { id } = useParams();
    const { data: studentData, isLoading: isStudentDataLoading } =
        useGetStudentDetailQuery(Number(id));
    const [studentCopy, setStudentCopy] = useState<Student>();

    const pieColors = [palette.primary[800], palette.primary[300]];
    const [isObservationModalOpen, setIsObservationModalOpen] =
        useState<boolean>(false);
    const [showAddObservationButton, setShowAddObservationButton] =
        useState<boolean>(false);

    const [showGenerateReportButton, setShowGenerateReportButton] =
        useState<boolean>(false);

    const handleOpenObservationModal = () => {
        setIsObservationModalOpen(true);
    };
    const handleCloseObservationModal = () => {
        setIsObservationModalOpen(false);
    };

    const handleObservationSubmitSuccess = () => {
        console.log("Observation submitted successfully");
        setIsObservationModalOpen(false);
        setShowAddObservationButton(false);
        setShowGenerateReportButton(true);
    };

    const capitalizeName = (name: string | undefined): string => {
        if (!name || name === "") return "";
        return name
            .split(" ")
            .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
            .join(" ");
    };

    const calculateAge = (dobString?: string): Age => {
        if (!dobString) {
            return { years: 0, months: 0 };
        }
        const dob = new Date(dobString);
        const now = new Date();

        let years = now.getFullYear() - dob.getFullYear();
        let months = now.getMonth() - dob.getMonth();

        // Adjust years and months if the current month is before the birth month
        if (months < 0) {
            years--;
            months += 12;
        }

        // Adjust months if the current day is before the birth day
        if (now.getDate() < dob.getDate()) {
            if (months === 0) {
                years--;
                months = 11;
            } else {
                months--;
            }
        }

        return { years, months };
    };

    const [
        triggerGeneratePdf,
        { data: pdfBlob, error, isLoading: isPdfLoading },
    ] = useLazyGetStudentReportPdfQuery();
    const [pdfUrl, setPdfUrl] = useState<string | null>(null);
    const iframeRef = useRef<HTMLIFrameElement>(null);
    const navigate = useNavigate();
    useEffect(() => {
        if (pdfBlob) {
            const url = URL.createObjectURL(pdfBlob);
            setPdfUrl(url);
            saveAs(pdfBlob, `${studentData?.name}Report.pdf`);
        }
    }, [pdfBlob]);

    useEffect(() => {
        console.log("studentData in use effect", studentData);
        if (
            studentData &&
            studentData.aggregateData &&
            studentData.aggregateData.length !== 0
        ) {
            if (!studentData.aggregateData[0].observation) {
                console.log("setting observation button true");
                setShowAddObservationButton(true);
                setShowGenerateReportButton(false);
            } else if (studentData.aggregateData[0].observation) {
                setShowGenerateReportButton(true);
                setShowAddObservationButton(false);
            }

        }
        if (studentData && studentData.name) {
            let copy: Student = {
                ...studentData,
                name: capitalizeName(studentData.name),
            };
            setStudentCopy(copy);
        }
    }, [studentData]);

    const downloadAndPrint = async () => {
        try {
            const result = await triggerGeneratePdf(Number(id));
            if (result.data) {
                const url = URL.createObjectURL(result.data);
                setPdfUrl(url);
                handlePrint();
            }
        } catch (error) {
            console.error("Failed to generate pdf", error);
        }
    };

    const handlePrint = useReactToPrint({
        content: () => iframeRef.current,
        documentTitle: `${studentData?.name}Report`,
    });

    const openStudentReportPage = ()=>{
        navigate(`/studentReport/${studentData?.id}`);
    };

    const handleGeneratePdf = () => {
        triggerGeneratePdf(Number(id));
    };
    const overallPerformance =
        studentData?.aggregateData?.[0]?.overallPerformance || 0;

    const overallPerformanceTotal=
        studentData?.aggregateData?.[0]?.overallPerformanceTotal || 0;

    const overallPerformancePercentage = Math.round((overallPerformance / overallPerformanceTotal)*100)

    
    return (
        <Box m="1.5rem 2.5rem" width="100%">
            <Header
                title={capitalizeName(studentData?.name) || "Student"}
                subtitle=""
            />

            <Box
                display="flex"
                flexDirection="column"
                alignItems="left"
                justifyContent="left"
                columnGap={2}
            >
                <Box display="flex"
                    flexDirection="row"
                    gap={5}
                    alignItems="center"
                >

                    <Box>

                        <Typography variant="h4"> Class: {studentData?.class} {studentData?.section}</Typography>
                        <Typography variant="h4">Gender: {studentData?.gender}</Typography>
                        <Typography variant="h4">
                            Age: {calculateAge(studentData?.dateOfBirth).years} years and{" "}
                            {calculateAge(studentData?.dateOfBirth).months} months
                        </Typography>
                    </Box>

                    <Box>
                        {studentData?.aggregateData && studentData.aggregateData.length > 0 && (
                            <Box mb={3} display="flex" alignItems="right" flexDirection="column">
                               
                                <PieChart width={100} height={100}>
                                    <Pie

                                        data={[
                                            { name: "Correct", value: overallPerformancePercentage },
                                            { name: "Incorrect", value: 100 - overallPerformancePercentage }
                                        ]}
                                        dataKey="value"
                                        innerRadius={30}
                                        outerRadius={40}
                                        paddingAngle={2}
                                        stroke="none"
                                        labelLine={false}
                                        label={({ cx, cy }) => (
                                            <text
                                                x={cx}
                                                y={cy}
                                                textAnchor="middle"
                                                dominantBaseline="middle"
                                                fontSize="1.2rem"
                                                fontWeight="bold"
                                                fill={palette.primary[800]}
                                            >
                                                {overallPerformancePercentage}%
                                            </text>
                                        )}
                                    >
                                        <Cell fill={'#FF0000'} />
                                        <Cell fill={'#06ad06'} />
                                    </Pie>
                                </PieChart>
                                <Typography variant="h6" sx={{ minWidth: 180 }}>
                                    Overall Performance
                                </Typography>
                            </Box>
                        )}
                    </Box>

                </Box>
                <StudentReport data={studentData?.aggregateData} />
            </Box>
            <FlexBetween margin="1.5rem 1rem 0 1rem" width={600}>
                { console.log(showAddObservationButton)}
                {showAddObservationButton && (
                    <Button
                        onClick={handleOpenObservationModal}
                        sx={{
                            backgroundColor: '#7B2CBF',
                            '&:hover': { backgroundColor: '#6a23b5' },
                            borderRadius: '8px',
                            paddingX: 4,
                            paddingY: 1.2,
                            fontSize: '1rem,'
                        }}
                    >
                        Add Observation
                    </Button>
                )}
                {showGenerateReportButton && (
                <Button
                    onClick={openStudentReportPage}
                    sx={{
                        backgroundColor: '#7B2CBF',
                        '&:hover': { backgroundColor: '#6a23b5' },
                        borderRadius: '8px',
                        paddingX: 4,
                        paddingY: 1.2,
                        fontSize: '1rem,'
                    }}
                >
                    View Report
                    </Button>
                )}
            </FlexBetween>
            <ObservationModal
                onClose={handleCloseObservationModal}
                open={isObservationModalOpen}
                onSubmitSuccess={handleObservationSubmitSuccess}
                student={studentData ?? null}
            />
           
        </Box>
    );
};

export default StudentDetailPage;
